// 23. 10. 2026 (owner): after an update, the players who played before it get one popup with what changed (`whatsNew` in
// /api/me, shared `whatsNew.ts`). Only on Camp, never over the first session's guided steps, a fight or a run's choice;
// closing it (Got it, the cross, Escape or a tap outside) tells the server, and it does not come back for this update.

import { useState } from 'react';
import { useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { MeDTO } from '@lastday/shared';
import { api } from '@/api/client';
import { qk, useMe } from '@/api/hooks';
import { Button } from '@/components/ui';
import { Sheet } from '@/components/Sheet';
import { t } from '@/i18n';

export function WhatsNew() {
  const me = useMe().data;
  const s = me?.survivor;
  const news = me?.whatsNew ?? null;
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const [closed, setClosed] = useState<string | null>(null);
  const busy = !!s?.pendingEncounterId || !!s?.pendingEventExpeditionId;
  const open = !!news && !!s && !s.guided && !busy && pathname === '/camp' && closed !== news.id;
  if (!open || !news) return null;

  const close = () => {
    setClosed(news.id);
    qc.setQueryData<MeDTO>(qk.me, (m) => (m ? { ...m, whatsNew: null } : m));
    void api.post('/whats-new/seen', { id: news.id }).catch(() => undefined);
  };

  return (
    <Sheet
      open
      onClose={close}
      maxWidth={520}
      title={
        <span className="flex items-baseline gap-2">
          <span className="mono rounded-[2px] bg-text px-1.5 text-[11px] font-bold uppercase leading-[17px] tracking-[0.1em] text-bg">{t('common.whatsNew.tag')}</span>
          <span data-testid="whats-new-title">{news.title}</span>
        </span>
      }
    >
      <div className="flex flex-col" data-testid="whats-new" data-news={news.id}>
        <div className="flex flex-col gap-3 px-4 py-4">
          <p className="text-[14px] leading-5 text-muted">{news.intro}</p>
          <ul className="flex flex-col gap-3">
            {news.items.map((x) => (
              <li key={x.title} className="flex flex-col gap-0.5 border-l-2 border-line pl-3" data-testid="whats-new-item">
                <span className="text-[15px] font-semibold">{x.title}</span>
                <span className="text-[14px] leading-5 text-muted">{x.text}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* The sheet scrolls the list; Got it stays at its bottom. */}
        <div className="sticky bottom-0 z-[2] border-t border-border bg-surface px-4 pb-1 pt-3 shadow-[0_16px_0_var(--color-surface)]">
          <Button full onClick={close} data-testid="whats-new-ok">
            {t('common.whatsNew.ok')}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
